<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />

   <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      title="Filter payments"
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   >
      <!-- Client -->
      <SelectField
         v-model="filterDraft.clientId"
         col="mb-3"
         label="Client"
         icon="fa fa-user"
         :options="clientsOptions"
         :reduce="c => Number(c.id)"
         option-label="label"
         placeholder="All clients"
      />

      <!-- Method -->
      <SelectField
         v-model="filterDraft.method"
         col="mb-3"
         label="Method"
         icon="fa fa-credit-card"
         :options="allMethodOptions"
         :reduce="m => m.value"
         option-label="label"
         placeholder="All methods"
      />
   </FilterModal>
   <!--End Filter Data Modal-->


   <!--Record / Edit Payment Modal-->
   <RecordFormModal
      :title="isEditing ? 'Edit Payment' : 'Record Payment'"
      title-icon="fa fa-money"
      size="lg"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      :show-sample="false"
      :show-reset="false"
      @save="saveOrEditRecord"
   >
      <!-- Client (new payments only) -->
      <div class="row g-3 mb-0">
         <SelectField
            v-if="!isEditing"
            v-model="formInputs.client_id"
            col="col-md-12"
            name="client_id"
            label="Client"
            icon="fa fa-user"
            :options="clientsOptions"
            :reduce="c => Number(c.id)"
            option-label="label"
            placeholder="Select client"
            tooltip="Please select a client."
            :hint="selectedClient
               ? 'Current plan ends: ' + (selectedClient.subscription_end_date ? $ConvertToLocalDate(selectedClient.subscription_end_date) : 'no plan')
               : ''"
            :submitted="submitted"
            required
            @change="onPlanInputsChange"
         />
         <div v-else class="col-md-12">
            <label class="form-label">Client</label>
            <div class="form-control-plaintext sa-core-color">
               {{ editingRecord?.client_name }} <span class="text-muted">— {{ editingRecord?.company_name }}</span>
            </div>
         </div>
      </div>

      <!-- Plan (new payments only) -->
      <template v-if="!isEditing">
         <div class="row g-3 mb-0 mt-1">
            <CheckField
               v-model="formInputs.apply_plan"
               col="col-12"
               text="This payment is for a plan (activate / extend the subscription)"
               @change="$nextTick(onPlanInputsChange)"
            />
         </div>

         <div v-if="formInputs.apply_plan" class="row g-3 mb-0">
            <SelectField
               v-model="formInputs.package_id"
               col="col-md-6"
               name="package_id"
               label="Plan"
               icon="fa fa-cube"
               :options="plansOptions"
               :reduce="p => Number(p.id)"
               :get-option-label="p => `${p.subscription_package} (${formInputs.currency || 'KES'} ${formatMoney(planPrice(p))}/month)`"
               placeholder="Select plan"
               tooltip="Please select a plan."
               :submitted="submitted"
               required
               @change="onPlanInputsChange"
            />
            <SelectField
               v-model="formInputs.period"
               col="col-md-6"
               name="period"
               label="Period"
               icon="fa fa-refresh"
               :options="periodOptions"
               :reduce="o => o.value"
               option-label="label"
               placeholder="Select period"
               tooltip="Please select a period."
               :submitted="submitted"
               required
               @change="onPlanInputsChange"
            />
            <div v-if="planEndPreview" class="col-12">
               <small class="text-success">
                  <i class="fa fa-calendar-check-o me-1"></i>
                  Plan will run until <strong>{{ planEndPreview }}</strong>
                  <span v-if="extendsRunningPlan">(added on top of the days left)</span>
               </small>
            </div>
         </div>
      </template>

      <!-- Money -->
      <div class="row g-3 mb-0 mt-1">
         <MoneyField
            v-model="formInputs.amount"
            col="col-md-4"
            min="0"
            label="Amount"
            icon="fa fa-money"
            :currency="formInputs.currency"
            tooltip="Please enter the amount."
            required
            @input="handleInputChange"
         >
            <small v-if="suggestedAmount !== null && Number(formInputs.amount) !== suggestedAmount" class="text-muted sa-core-color">
               Plan price: {{ formInputs.currency }} {{ formatMoney(suggestedAmount) }}
               <a href="#" @click.prevent="formInputs.amount = suggestedAmount">use</a>
            </small>
         </MoneyField>
         <FormField
            v-model="formInputs.currency"
            col="col-md-2"
            maxlength="3"
            pattern="[A-Za-z]{3}"
            class="text-uppercase"
            label="Currency"
            icon="fa fa-globe"
            tooltip="A 3-letter code, e.g. KES or USD."
            required
            @input="handleInputChange"
            @change="onPlanInputsChange"
         />
         <DateField
            v-model="formInputs.paid_at"
            col="col-md-6"
            label="Paid At"
            icon="fa fa-clock-o"
            max="today"
            tooltip="Please choose when it was paid."
            required
            with-time
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Method & Status -->
      <div class="row g-3 mb-0">
         <SelectField
            v-model="formInputs.payment_method"
            col="col-md-6"
            name="payment_method"
            label="Method"
            icon="fa fa-credit-card"
            :options="methodOptions"
            :reduce="o => o.value"
            option-label="label"
            placeholder="Select method"
            tooltip="Please select a method."
            :submitted="submitted"
            required
         />
         <SelectField
            v-model="formInputs.payment_status"
            col="col-md-6"
            name="payment_status"
            label="Status"
            icon="fa fa-check-circle"
            :options="statusOptions"
            :reduce="o => o.value"
            option-label="label"
            placeholder="Select status"
            :clearable="false"
            tooltip="Please select a status."
            :submitted="submitted"
            required
         />
      </div>

      <!-- Reference & Phone -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.transaction_reference"
            col="col-md-6"
            label="Reference"
            icon="fa fa-barcode"
            hint="M-Pesa code, bank ref…"
         />
         <FormField
            v-model="formInputs.phone_number"
            col="col-md-6"
            type="tel"
            inputmode="numeric"
            label="Phone (optional)"
            icon="fa fa-phone"
         />
      </div>

      <!-- Description -->
      <div class="row g-3 mb-0">
         <TextareaField
            v-model="formInputs.description"
            col="col-12"
            label="Description (optional)"
            icon="fa fa-comment"
            rows="2"
            maxlength="500"
         />
      </div>

      <p v-if="isEditing" class="small text-muted mt-2 mb-0 sa-core-color">
         Editing only corrects the payment details. It does not change the client's plan.
      </p>
   </RecordFormModal>
   <!--End Record / Edit Payment Modal-->   


   <!--Compensation Modal-->
   <form id="compensationForm" novalidate @submit.prevent="saveCompensation()">
      <div id="compensationModal" class="modal fade" tabindex="-1" data-bs-focus="false">
         <div class="modal-dialog">
            <div class="modal-content bg-sa-modal-content">
               <div class="modal-header bg-sa-modal-header">
                  <h4 class="modal-title sa-core-color"><i class="fa fa-gift me-2"></i>Add Free Days</h4>
                  <button type="button" class="btn-close color-sa-btn-close" data-bs-dismiss="modal"></button>
               </div>
               <div class="modal-body">
                  <AlertComponent v-if="defaultAlert.pageOrModal == 'modal'" :alertstatus="defaultAlert.alertstatus" :message="defaultAlert.alertMessage" :type="defaultAlert.alertType" />

                                    <div class="row g-3 mb-0">
                     <SelectField
                        v-model="formInputs.client_id"
                        col="col-md-12"
                        name="comp_client_id"
                        label="Client"
                        icon="fa fa-user"
                        :options="clientsOptions"
                        :reduce="c => Number(c.id)"
                        option-label="label"
                        placeholder="Select client"
                        tooltip="Please select a client."
                        :submitted="submitted"
                        required
                     />
                     <FormField
                        v-model="formInputs.days"
                        col="col-md-12"
                        type="number"
                        min="1"
                        max="365"
                        label="Days"
                        icon="fa fa-calendar-plus-o"
                        tooltip="Please enter the number of days."
                        required
                        @input="handleInputChange"
                     />
                     <FormField
                        v-model="formInputs.reason"
                        col="col-md-12"
                        label="Reason"
                        icon="fa fa-comment"
                        placeholder="e.g. System downtime on 12 Oct"
                        tooltip="Please enter the reason."
                        required
                        @input="handleInputChange"
                     />
                  </div>

                  <small v-if="compensationEndPreview" class="d-block text-success mt-2">
                     <i class="fa fa-calendar-check-o me-1"></i>Plan will end <strong>{{ compensationEndPreview }}</strong>
                  </small>
                  <small v-if="selectedClient && !Number(selectedClient.subscription_package)" class="d-block text-warning mt-2">
                     This client has no plan. Record a payment with a plan first.
                  </small>
               </div>
               <div class="modal-footer bg-sa-modal-footer">
                  <button type="submit" class="btn btn-success me-2" :disabled="isSaving">
                     <i class="fa fa-plus me-1"></i>Add Days
                  </button>
                  <button type="button" class="btn btn-default modal_close color-sa-modal_close" data-bs-dismiss="modal">Close</button>
               </div>
            </div>
         </div>
      </div>
   </form>
   <!--End Compensation Modal-->

   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>

   <!-- Monthly summary -->
   <div class="card glass bg-sa-card mb-2">
      <div class="card-body bg-sa-card-body p-2">
         <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
            <label for="summaryMonth" class="sa-core-color mb-0">Month</label>
            <!-- Month only (DateField month): "2026-10" -->
            <DateField
               v-model="summaryMonth"
               id="summaryMonth"
               col=""
               month
               compact
               class="form-control-sm month-box"
               @change="onSummaryMonthChange"
            />
         </div>
         <!-- What the tiles add up: the same payments as the table -->
         <small class="d-block sa-core-color opacity-75 mb-2"><i class="fa fa-filter me-1"></i>Totals for: {{ summaryPeriodLabel }}</small>
         <div class="row g-2">
            <div class="col-6 col-md-3">
               <div class="summary-tile">
                  <div class="summary-label">Cash received</div>
                  <div v-if="summary && Object.keys(summary.received).length" class="summary-value">
                     <div v-for="(amount, currency) in summary.received" :key="currency">{{ currency }} {{ formatMoney(amount) }}</div>
                  </div>
                  <div v-else class="summary-value">0.00</div>
               </div>
            </div>
            <div class="col-6 col-md-3">
               <div class="summary-tile">
                  <div class="summary-label">Payments</div>
                  <div class="summary-value">{{ summary?.payments_count ?? 0 }}</div>
               </div>
            </div>
            <div class="col-6 col-md-3">
               <div class="summary-tile">
                  <div class="summary-label">Pending</div>
                  <div v-if="summary && Object.keys(summary.pending).length" class="summary-value text-warning">
                     <div v-for="(amount, currency) in summary.pending" :key="currency">{{ currency }} {{ formatMoney(amount) }}</div>
                  </div>
                  <div v-else class="summary-value">0.00</div>
               </div>
            </div>
            <div class="col-6 col-md-3">
               <div class="summary-tile">
                  <div class="summary-label">Free days given</div>
                  <div class="summary-value">{{ summary?.compensation_days ?? 0 }} <small>({{ summary?.compensation_count ?? 0 }}×)</small></div>
               </div>
            </div>
         </div>
      </div>
   </div>

   <!--Table-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">

                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            @add="openNewPayment()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Subscriptions</h6></li>
               <li>
                  <button type="button" class="dropdown-item" @click="openCompensation()" data-bs-toggle="modal" data-bs-target="#compensationModal">
                     <i class="fa fa-gift fa-fw me-1 text-info"></i> Add Free Days
                  </button>
               </li>

               <li><hr class="dropdown-divider"></li>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>

         <!-- Table Search Panel -->

         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />

         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />

         <div class="table-responsive">
            <table
               class="table table-hover align-middle mb-0"
               id="bootstrapTable"
               tabindex="0"
               @keydown.up="$moveRowSelection(-1, $event)"
               @keydown.down="$moveRowSelection(1, $event)"
               @keydown.enter="$onTableEnter($event)"
            >
               <thead>
                  <tr>
                     <th><input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" /></th>
                     <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
                     <th class="button hide-sm-td">No</th>
                     <SortableTh v-if="isColumnVisible('provider_transaction_date')" column="provider_transaction_date" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Paid At</SortableTh>
                     <th v-if="isColumnVisible('client')">Client</th>
                     <SortableTh v-if="isColumnVisible('amount')" column="amount" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Amount</SortableTh>
                     <SortableTh v-if="isColumnVisible('payment_method')" column="payment_method" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Method</SortableTh>
                     <SortableTh v-if="isColumnVisible('payment_status')" column="payment_status" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
                     <th v-if="isColumnVisible('for')" class="hide-sm-td">For</th>
                     <th class="button">Action</th>
                  </tr>
               </thead>

               <tbody>
                  <template v-for="(record, index) in tableData.records" :key="record.id">
                     <!-- Main row -->
                     <tr class="clickable-row"
                         :data-record-id="record.id"
                         :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                         @click="$onRowClick(record, $event)"
                         @dblclick="!isFreeEntry(record) && $onRowDblClick(record, $event)"
                     >
                        <td><input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" /></td>
                        <td class="expand-cell hide-sm-td" :aria-expanded="expandedRows[record.id] ? 'true' : 'false'" @click="$toggleDetails(record.id)">
                           <i class="fa fa-lg expand-icon ms-1"
                              :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                              :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                        </td>
                        <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                        <td v-if="isColumnVisible('provider_transaction_date')" class="hide-sm-td">{{ formatDate(record.provider_transaction_date) }}</td>
                        <td v-if="isColumnVisible('client')">
                           <!-- Phones: + / − and the number go in front of the client (their columns are hidden) -->
                           <span
                              class="show-sm-inline expand-cell me-2"
                              :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                              @click="$toggleDetails(record.id)"
                           >
                              <i class="fa fa-lg expand-icon"
                                 :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                                 :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                           </span>
                           <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.client_name || '-' }}
                           <div class="small text-muted sa-core-color">{{ record.company_name }}</div>
                        </td>
                        <td v-if="isColumnVisible('amount')" class="text-nowrap hide-sm-td">
                           <span v-if="isFreeEntry(record)" class="text-muted">—</span>
                           <span v-else>{{ record.currency }} {{ formatMoney(record.amount) }}</span>
                        </td>
                        <td v-if="isColumnVisible('payment_method')" class="hide-sm-td"><span :class="methodClass(record.payment_method)">{{ methodLabel(record.payment_method) }}</span></td>
                        <td v-if="isColumnVisible('payment_status')" class="hide-sm-td"><span :class="statusClass(record.payment_status)">{{ statusLabel(record.payment_status) }}</span></td>
                        <td v-if="isColumnVisible('for')" class="hide-sm-td">{{ forLabel(record) }}</td>
                        <td>
                           <RowActions
                              :record="record"
                              :modal-id="'deletePaymentModal' + record.id"
                              :can-edit="!isFreeEntry(record)"
                              :can-soft-delete="true"
                              :can-restore="true"
                              @edit="editRecord"
                              @delete-type="setDeleteType"
                           />

                           <!--Delete Modal-->
                           <DeleteModal
                              :modal-id="'deletePaymentModal' + record.id"
                              :title="deleteModalTitle"
                              :message="deleteModalMessage"
                              @confirm="deleteRecord(record.id)"
                           >
                              <p v-if="tableData.deleteType === 'soft'" class="small text-muted text-center mb-0">
                                 This hides the payment from reports. It does not change the client's plan.
                              </p>
                           </DeleteModal>
                           <!--End Delete Modal-->
                        </td>
                     </tr>

                     <!-- Hidden expanded row -->
                     <tr class="collapse-row">
                        <td class="p-0"></td>
                        <td class="p-0 hide-sm-td"></td>
                        <td colspan="8" class="p-0">
                           <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                              <div class="p-3 details-list">
                                 <div><i class="fa fa-hashtag"></i><strong>Payment Number:</strong> {{ record.payments_transaction_number }}</div>

                                 <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                                 <div :class="{ 'show-sm-td': isColumnVisible('provider_transaction_date') }"><i class="fa fa-clock-o"></i><strong>Paid At:</strong> {{ formatDate(record.provider_transaction_date) }}</div>
                                 <div :class="{ 'show-sm-td': isColumnVisible('client') }"><i class="fa fa-user"></i><strong>Client:</strong> {{ record.client_name || '-' }} <span class="text-muted">— {{ record.company_name }}</span></div>
                                 <div :class="{ 'show-sm-td': isColumnVisible('amount') }">
                                    <i class="fa fa-money"></i><strong>Amount:</strong>
                                    <span v-if="isFreeEntry(record)" class="text-muted">—</span>
                                    <span v-else>{{ record.currency }} {{ formatMoney(record.amount) }}</span>
                                 </div>
                                 <div :class="{ 'show-sm-td': isColumnVisible('payment_method') }"><i class="fa fa-credit-card"></i><strong>Method:</strong> {{ methodLabel(record.payment_method) }}</div>
                                 <div :class="{ 'show-sm-td': isColumnVisible('payment_status') }"><i class="fa fa-check-circle"></i><strong>Status:</strong> {{ statusLabel(record.payment_status) }}</div>
                                 <div :class="{ 'show-sm-td': isColumnVisible('for') }"><i class="fa fa-cube"></i><strong>For:</strong> {{ forLabel(record) }}</div>

                                 <!-- Plan / compensation details -->
                                 <div v-if="record.meta?.covers_from"><i class="fa fa-calendar"></i><strong>Covers From:</strong> {{ $ConvertToLocalDate(record.meta.covers_from) }}</div>
                                 <div v-if="record.meta?.covers_to"><i class="fa fa-calendar-times-o"></i><strong>Plan Ends:</strong> {{ $ConvertToLocalDate(record.meta.covers_to) }}</div>
                                 <div v-if="record.meta?.previous_end"><i class="fa fa-history"></i><strong>Previous End:</strong> {{ $ConvertToLocalDate(record.meta.previous_end) }}</div>

                                 <!-- Always in details -->
                                 <div v-if="record.transaction_reference"><i class="fa fa-barcode"></i><strong>Reference:</strong> {{ record.transaction_reference }}</div>
                                 <div v-if="record.mpesa_receipt_number"><i class="fa fa-mobile"></i><strong>M-Pesa Receipt:</strong> {{ record.mpesa_receipt_number }}</div>
                                 <div v-if="record.phone_number"><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone_number }}</div>
                                 <div v-if="record.customer_email"><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.customer_email }}</div>
                                 <div v-if="record.description"><i class="fa fa-comment"></i><strong>{{ record.payment_method === 'compensation' ? 'Reason' : 'Description' }}:</strong> {{ record.description }}</div>
                                 <div v-if="record.result_description"><i class="fa fa-info-circle"></i><strong>Provider Message:</strong> {{ record.result_description }}</div>
                                 <div><i class="fa fa-user"></i><strong>Recorded By:</strong> {{ record.created_by_name || 'System' }}</div>
                                 <div><i class="fa fa-calendar"></i><strong>Recorded On:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                                 <div v-if="record.updated_by_name"><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updated_by_name }}</div>
                                 <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                              </div>
                           </div>
                        </td>
                     </tr>
                  </template>

                  <!-- No rows: blank while loading, then the empty message in the middle -->
                  <tr v-if="tableData.records.length === 0" class="table-filler">
                     <td colspan="10" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                        <TableEmptyState
                           v-if="!tableLoading"
                           :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                           :can-add="true"
                           :title="summaryMonth ? `No payments in ${summaryMonthLabel}` : ''"
                           :message="summaryMonth ? 'Pick another month, or clear the filters to see all payments.' : ''"
                           icon="fa fa-money"
                           @add="openNewPayment()"
                           @clear="clearAllFilterChips"
                        />
                     </td>
                  </tr>
               </tbody>

               <tfoot class="table-light">
                  <tr>
                     <td colspan="10">
                        <!-- Pagination (hidden when there are no rows; its space is kept) -->
                        <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                        <Pagination
                           v-else
                           :currentPage="tableData.currentPage"
                           :totalPages="tableData.totalPages"
                           :hasNextPage="tableData.hasNextPage"
                           :totalRecords="tableData.totalRecords"
                           :startRecord="tableData.startRecord"
                           :endRecord="tableData.endRecord"
                           :showFullPagination="tableData.totalRecords > 0"
                           :lastKnownPage="lastKnownPage"
                           @page-changed="fetchRecords"
                           @loaderMessage="updateLoaderMessage"
                           @last-page-request="goToLastPage"
                        />
                     </td>
                  </tr>
               </tfoot>
            </table>
         </div>
      </div>
   </div>
   <!--End Table-->
</template>

<script>
import axios from "axios";

import AlertComponent from "../../../components/ui/AlertComponent.vue";
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import CheckField from "../../../components/admin/CheckField.vue";
import MoneyField from "../../../components/admin/MoneyField.vue";
import DateField from "../../../components/admin/DateField.vue";
import TextareaField from "../../../components/admin/TextareaField.vue";
import tableMixin from "@/mixins/tableMixin.js";
import clientsPaymentsMixin from "./mixins/clientsPaymentsMixin.js";


export default {
   name: "ClientsPayments",
   mixins: [tableMixin, clientsPaymentsMixin], // shared table methods first, then ClientsPayments' own
   components: {
      AlertComponent,
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      RecordFormModal,
      FormField,
      SelectField,
      CheckField,
      MoneyField,
      DateField,
      TextareaField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert ...) comes from tableMixin
         standardPageSetup: true,                      // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/clients/api_clients_payments.php",  // used by tableMixin's fetchRecords, delete
         columnsKey: "clientspayments",                // saves this page's hidden columns (Columns button)
         extraFilterKeys: ["clientId", "method"],      // extra filters in the Filter window
         recordFilterLabels: { byme: "Recorded by me", deletedone: "Deleted payments" }, // "View:" label
         editingRecord: null,
         formInputs: this.getDefaultFormInputs(),
         pageIcon: "fa fa-money", // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Payments", value: "all" },
            { label: "Recorded By Me", value: "byme" },
            { label: "Deleted Payments", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Payment Number", value: "payments_transaction_number" },
            { label: "Reference", value: "transaction_reference" },
            { label: "M-Pesa Receipt", value: "mpesa_receipt_number" },
            { label: "Phone", value: "phone_number" },
            { label: "Email", value: "customer_email" },
            { label: "Description", value: "description" },
         ],
         clientsOptions: [],
         plansOptions: [],
         lastSuggestedAmount: null,
         lastCurrencyClientId: null, // the client whose currency was last filled in (Record Payment)
         summaryMonth: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`, // "YYYY-MM", this month
         summary: null,
         lastSummaryKey: "", // the filters the tiles were last loaded for
         methodOptions: [
            { label: "Cash", value: "cash" },
            { label: "M-Pesa", value: "mpesa" },
            { label: "Bank", value: "bank" },
            { label: "PayPal", value: "paypal" },
            { label: "Card", value: "card" },
            { label: "Manual / Other", value: "manual" },
         ],
         statusOptions: [
            { label: "Completed", value: "completed" },
            { label: "Pending", value: "pending" },
            { label: "Failed", value: "failed" },
            { label: "Refunded", value: "refunded" },
         ],
         periodOptions: [
            { label: "Monthly", value: 1 },
            { label: "Quarterly", value: 3 },
            { label: "Yearly", value: 12 },
         ],
      };
   },
   mounted() {
      this.fetchClientsOptions();
      this.fetchPlansOptions();
      // The tiles load with the table (afterFetch in clientsPaymentsMixin.js)
   },
   // The table's dates changed somewhere else (Filter window, × on the chip): the Month box lets go
   watch: {
      "tableData.startDate"() {
         this.syncMonthWithTable();
      },
      "tableData.endDate"() {
         this.syncMonthWithTable();
      },
   },
   async created() {
      this.setTableMonth(this.summaryMonth); // the table starts on this month, like the tiles
      await this.fetchRecords(
         this.tableData.currentPage,
         this.tableData.currentSortColumn,
         this.tableData.currentSortDirection,
         this.tableData.startDate,
         this.tableData.endDate,
         this.$loaderMessages.loaderMessageDefault
      );
   },
   computed: {
      // "October 2026" / "01/10/2026 – 07/10/2026" / "All time", plus Client or Method when chosen
      summaryPeriodLabel() {
         const t = this.tableData;
         const day = (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "…");
         let period = "All time";
         if (this.summaryMonth) period = this.summaryMonthLabel;
         else if (t.startDate || t.endDate) period = `${day(t.startDate)} – ${day(t.endDate)}`;
         const extras = (this.pageFilterChips || []).map((chip) => chip.label);
         return [period, ...extras].join(" · ");
      },
      // "October 2026"
      summaryMonthLabel() {
         if (!this.summaryMonth) return "";
         const [year, month] = this.summaryMonth.split("-").map(Number);
         return new Date(year, month - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
      },
      // Page heading that follows the chosen filter
      pageTitle() {
         const titles = {
            all: "Client Payments",
            byme: "Client Payments Recorded By Me",
            deletedone: "Deleted Client Payments",
         };
         return titles[this.tableData.recordFilter] || "Client Payments";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
         return [
            { key: "provider_transaction_date", label: "Paid At" },
            { key: "client", label: "Client" },
            { key: "amount", label: "Amount" },
            { key: "payment_method", label: "Method" },
            { key: "payment_status", label: "Status" },
            { key: "for", label: "For" },
         ];
      },
      allMethodOptions() {
         return [
            ...this.methodOptions,
            { label: "Free days (compensation)", value: "compensation" },
            { label: "Free trial", value: "trial" },
         ];
      },
      selectedClient() {
         return this.clientsOptions.find((c) => Number(c.id) === Number(this.formInputs.client_id)) || null;
      },
      selectedPlan() {
         return this.plansOptions.find((p) => Number(p.id) === Number(this.formInputs.package_id)) || null;
      },
      // Plan price x months
      suggestedAmount() {
         if (this.isEditing || !this.formInputs.apply_plan || !this.selectedPlan || !this.formInputs.period) return null;
         return Math.round(this.planPrice(this.selectedPlan) * Number(this.formInputs.period) * 100) / 100;
      },
      // True when the client still has days left (new period is added on top)
      extendsRunningPlan() {
         const end = this.selectedClient?.subscription_end_date;
         return !!end && this.utcToDate(end) > new Date();
      },
      planEndPreview() {
         if (!this.selectedClient || !this.formInputs.period) return "";
         const start = this.extendsRunningPlan ? this.utcToDate(this.selectedClient.subscription_end_date) : new Date();
         const end = new Date(start);
         end.setMonth(end.getMonth() + Number(this.formInputs.period));
         return end.toLocaleString();
      },
      compensationEndPreview() {
         const days = Number(this.formInputs.days);
         if (!this.selectedClient || !Number(this.selectedClient.subscription_package) || !days) return "";
         const start = this.extendsRunningPlan ? this.utcToDate(this.selectedClient.subscription_end_date) : new Date();
         const end = new Date(start.getTime() + days * 86400000);
         return end.toLocaleString();
      },
   },
   methods: {
      // A plan's monthly price in the payment's currency: USD price for USD, otherwise the KES price
      planPrice(plan) {
         if (!plan) return 0;
         return String(this.formInputs.currency).toUpperCase() === "USD" ? Number(plan.price_usd) : Number(plan.subscription_price);
      },
      getDefaultFormInputs() {
         return {
            id: "",
            client_id: "",
            apply_plan: true,
            package_id: "",
            period: "",
            amount: null,
            currency: "KES",
            payment_method: "",
            payment_status: "completed",
            transaction_reference: "",
            phone_number: "",
            paid_at: this.nowForInput(),
            description: "",
            // Add Free Days
            days: "",
            reason: "",
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData() {
         return {
            ...this.baseTableData(),
            searchBy: "payments_transaction_number",
            searchPlaceHolderText: "Search by Payment Number",
            currentSortColumn: "provider_transaction_date",
            clientId: null, // Client filter (empty = all)
            method: "",     // Method filter (empty = all)
         };
      },
      async fetchClientsOptions() {

         try {
            const response = await axios.get(`${this.apiUrl}?action=fetchClients`, this.$handleConfig());
            if (response.data.status === "success") this.clientsOptions = response.data.results;
         } catch (error) {
            console.error("Unable to load clients", error);
         }
      },
      async fetchPlansOptions() {
         try {
            const response = await axios.get(`${this.apiUrl}?action=fetchPlans`, this.$handleConfig());
            if (response.data.status === "success") this.plansOptions = response.data.results;
         } catch (error) {
            console.error("Unable to load plans", error);
         }
      },
      /* ---------- Month box: drives the tiles AND the table ---------- */
      // "2026-10" -> the table's date range for that month (same format as the Filter window)
      monthRange(month) {
         const [year, m] = month.split("-").map(Number);
         const lastDay = new Date(year, m, 0).getDate();
         return { start: `${month}-01T00:00`, end: `${month}-${String(lastDay).padStart(2, "0")}T23:59` };
      },
      setTableMonth(month) {
         if (!month) return;
         const range = this.monthRange(month);
         this.tableData.datePreset = "custom";
         this.tableData.startDate = range.start;
         this.tableData.endDate = range.end;
      },
      // A month picked (or cleared) in the Month box
      onSummaryMonthChange() {
         if (this.summaryMonth) {
            this.setTableMonth(this.summaryMonth);
         } else {
            // Cleared: all payments (the tiles then show all-time totals)
            this.tableData.datePreset = "all";
            this.tableData.startDate = "";
            this.tableData.endDate = "";
         }
         this.tableData.currentPage = 1;
         this.fetchRecords(1, this.tableData.currentSortColumn, this.tableData.currentSortDirection);
      },
      // The table no longer shows exactly the chosen month (e.g. "Last week" in the Filter window):
      // empty the Month box. The tiles follow the table's own range
      syncMonthWithTable() {
         if (!this.summaryMonth) return;
         const range = this.monthRange(this.summaryMonth);
         if (this.tableData.startDate !== range.start || this.tableData.endDate !== range.end) {
            this.summaryMonth = "";
         }
      },
      // Summary for the chosen month, using the user's own month boundaries
      // force: reload even if the filters didn't change (after a save or delete)
      async fetchSummary(force = false) {
         const t = this.tableData;
         // The table's range in the user's own time ("2026-10-01" or "2026-10-01T00:00"), sent as UTC.
         // No range = all time. "to" is just after the end (the server counts up to, not including, "to")
         const local = (v, time) => new Date(String(v).includes("T") ? v : `${v}T${time}`);
         const from = t.startDate ? local(t.startDate, "00:00").toISOString() : "2000-01-01T00:00:00Z";
         const to = t.endDate ? new Date(local(t.endDate, "23:59").getTime() + 60000).toISOString() : "2100-01-01T00:00:00Z";
         const extra = this.pageFilterParams(); // Client and Method, the same as the table

         // Same filters as last time: the tiles are already right (paging, sorting, searching)
         const key = JSON.stringify([from, to, extra]);
         if (!force && key === this.lastSummaryKey) return;
         this.lastSummaryKey = key;

         try {
            const params = new URLSearchParams({ action: "fetchSummary", from, to, ...extra });
            const response = await axios.get(`${this.apiUrl}?${params}`, this.$handleConfig());
            if (response.data.status === "success") this.summary = response.data.results;
         } catch (error) {
            console.error("Unable to load summary", error);
         }
      },

      /* ---------- Record / edit payment ---------- */
      // Called by the Edit button, double-click and Alt+M
      editRecord(record) {
         if (this.isFreeEntry(record)) return;

         this.isEditing = true;
         this.editingRecord = record;
         this.formInputs = {
            ...this.getDefaultFormInputs(),
            id: record.id,
            client_id: Number(record.owner_user_id),
            apply_plan: false,
            amount: Number(record.amount),
            currency: record.currency || "KES",
            payment_method: record.payment_method,
            payment_status: record.payment_status,
            transaction_reference: record.transaction_reference || "",
            phone_number: record.phone_number || "",
            paid_at: record.provider_transaction_date ? this.toInputValue(this.utcToDate(record.provider_transaction_date)) : this.nowForInput(),
            description: record.description || "",
         };
         this.resetFormState();
      },
      async saveOrEditRecord() {
         if (this.isSaving) return;
         this.submitted = true;
         this.$scrollToTopModal("recordFormModal");

         if (!this.$validateForm("#recordForm")) {
            this.$playErrorSound();
            this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
            return;
         }

         this.isSaving = true;
         this.handleAlert("starting", "Please wait, saving...", "info", "modal");

         const f = this.formInputs;
         const formData = new FormData();
         formData.append("action", "saveOrEditRecord");
         formData.append("id", f.id);
         formData.append("client_id", f.client_id);
         formData.append("apply_plan", !this.isEditing && f.apply_plan ? 1 : 0);
         formData.append("package_id", f.package_id || "");
         formData.append("period", f.period || "");
         formData.append("amount", f.amount ?? "");
         formData.append("currency", (f.currency || "").toUpperCase());
         formData.append("payment_method", f.payment_method);
         formData.append("payment_status", f.payment_status);
         formData.append("transaction_reference", f.transaction_reference);
         formData.append("phone_number", f.phone_number);
         formData.append("paid_at", new Date(String(f.paid_at).replace(" ", "T")).toISOString()); // local -> UTC (DateField gives "2026-10-29 14:30")
         formData.append("description", f.description);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);

         try {
            const response = await axios.post(this.apiUrl, formData, this.$handleConfig());
            if (response.data.status === "success") {
               this.handleSaveOrEditSuccess(response);
            } else {
               this.handleAlert("ending", this.messageText(response.data.message), "danger", "modal");
            }
         } catch (error) {
            if (error.message === "Network Error") {
               this.handleAlert("ending", "No internet connection. Please check your network.", "danger", "modal");
            } else {
               this.handleAlert("ending", "Something went wrong. Please try again later.", "danger", "modal");
            }
         } finally {
            this.isSaving = false;
         }
      },

      /* ---------- Add Free Days ---------- */
      async saveCompensation() {
         if (this.isSaving) return;
         this.submitted = true;

         if (!this.$validateForm("#compensationForm")) {
            this.$playErrorSound();
            this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
            return;
         }

         this.isSaving = true;

         const formData = new FormData();
         formData.append("action", "addCompensation");
         formData.append("client_id", this.formInputs.client_id);
         formData.append("days", this.formInputs.days);
         formData.append("reason", this.formInputs.reason);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);

         try {
            const response = await axios.post(this.apiUrl, formData, this.$handleConfig());
            if (response.data.status === "success") {
               this.handleCompensationSuccess(response);
            } else {
               this.handleAlert("ending", this.messageText(response.data.message), "danger", "modal");
            }
         } catch (error) {
            this.handleAlert("ending", "Something went wrong. Please try again later.", "danger", "modal");
         } finally {
            this.isSaving = false;
         }
      },

      /* ---------- Export (own version: .xlsx file named with today's date) ---------- */
      async exportRecords(format) {
         const t = this.tableData;
         const formData = new FormData();
         formData.append("action", "exportRecords");
         formData.append("format", format);
         formData.append("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
         formData.append("page", 1);
         formData.append("limit", t.recordsPerPage);
         formData.append("searchBy", t.searchBy);
         formData.append("search", t.searchQuery);
         formData.append("orderBy", t.currentSortColumn);
         formData.append("orderDirection", t.currentSortDirection);
         formData.append("filter", t.recordFilter);
         this.appendFilterFormData(formData); // dates, client, method (tableMixin)
         // Columns hidden with the Columns button: the export leaves them out too
         formData.append("hiddenColumns", (this.hiddenColumns || []).join(","));

         this.tableData.isVisible = true;
         try {
            const response = await axios.post(this.apiUrl, formData, this.$handleConfig(null, "blob"));

            // The server answers with JSON when something goes wrong
            if (response.data.type && response.data.type.includes("application/json")) {
               const json = JSON.parse(await response.data.text());
               this.handleAlert("ending", this.messageText(json.message), "danger", "page");
               return;
            }

            const ext = { xls: "xlsx", csv: "csv", pdf: "pdf" }[format];
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `client_payments_${new Date().toISOString().slice(0, 10)}.${ext}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
         } catch (error) {
            this.handleAlert("ending", "Export failed. Please try again.", "danger", "page");
         } finally {
            this.tableData.isVisible = false;
         }
      },
   },
};
</script>